import { X, Users } from 'lucide-react';

/**
 * Sjednocená hlavička karty cockpitu (Pre / Starting / Průběh / Ukončení).
 * Fáze se nepíše textem — nese ji barva: tenký proužek vlevo + nádech karty
 * (viz `phaseCardStyle`). Název + datum vlevo, avatary týmu + chat vpravo.
 */

const AVATAR_GRADIENTS = [
    'linear-gradient(135deg, #1D9E75, #10B981)',
    'linear-gradient(135deg, #6C4192, #975ACD)',
    'linear-gradient(135deg, #F59E0B, #FF8A00)',
    'linear-gradient(135deg, #3B82F6, #06B6D4)',
    'linear-gradient(135deg, #EC4899, #A855F7)',
    'linear-gradient(135deg, #14B8A6, #0CC0DF)',
];

export function avatarGradient(id) {
    return AVATAR_GRADIENTS[Math.abs(Number(id) || 0) % AVATAR_GRADIENTS.length];
}

export function initials(jmeno, prijmeni) {
    return `${jmeno?.[0] ?? ''}${prijmeni?.[0] ?? ''}`.toUpperCase();
}

/**
 * Styl obalu karty s jemným nádechem barvy fáze. Spread do `style` div karty
 * spolu s `padding` / `display` / `gap`.
 */
export function phaseCardStyle(phaseColor, isDark) {
    const base = isDark ? '#1a1a1f' : '#ffffff';
    return {
        background: `linear-gradient(150deg, ${phaseColor}1F, ${phaseColor}0A 55%, ${phaseColor}00), ${base}`,
        border: `1px solid ${phaseColor}40`,
        borderRadius: 12,
    };
}

/**
 * Compact 3-col tile (passive) — Čas / Auto / Pokladna. Použito v Pre (3×
 * info), Starting/Active (1× pro čas + 2× klikatelné `CockpitSlot compact`
 * pro auto/pokladnu). Sjednocuje rozměry napříč fázemi.
 */
export function CockpitInfoCol({ Icon, accent = '#94A3B8', label, value, valueColor, isDark }) {
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    return (
        <div style={{
            flex: 1, minWidth: 0,
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '9px 9px', borderRadius: 9,
            border: `1px solid ${accent}55`,
            background: isDark ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.03)',
        }}>
            <span style={{
                width: 26, height: 26, borderRadius: 5, flexShrink: 0,
                background: `${accent}1A`, color: accent,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {Icon ? <Icon size={14} /> : null}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{
                    fontSize: 8, fontWeight: 700, letterSpacing: 0.3,
                    textTransform: 'uppercase', color: labelMuted,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{label}</span>
                <span style={{
                    fontSize: 12, fontWeight: 700, color: valueColor || (isDark ? '#fff' : 'var(--text-primary)'),
                    fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
                    display: 'flex', alignItems: 'center',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{value}</span>
            </div>
        </div>
    );
}

/**
 * Geometrie hlavičky na mobilu. `StalyPruh` z ní skládá pás akce ve spodní
 * hraně (vystoupí z odsazení na plnou šířku a vezme si zaoblení).
 */
export const HLAVICKA_MOBIL = { odstup: 0, odsazeni: 14, zaobleni: 24, hrana: 1 };

/**
 * Stálý pruh cockpitu (návrh „Horní panel směny", 2026-10-12). Na mobilu
 * **deska v barvě plochy přes celou šířku**: nahoře nemá hranu ani odstup
 * od boků, takže splyne se stavovým řádkem a kolem ostrůvku není vidět
 * žádný pruh. Od stránky ji odděluje jen zaoblený spodek, tenká linka
 * a stín. Barvu nese obsah (tečka stavu, osa fází, ikony dlaždic), ne plocha.
 *
 * `background-color` musí být plná barva plochy: iOS podle ní barví
 * stavový řádek aplikace z plochy. Dřívější skleněný závoj (bílá 7 %)
 * z něj dělal šedý pruh nad hlavičkou a fialová záře v rohu soupeřila
 * s obsahem.
 *
 * Desktop zůstává `phaseCardStyle` — tam je to pořád karta.
 */
export function phaseSectionStyle(phaseColor, isDark, isMobile) {
    if (!isMobile) return { ...phaseCardStyle(phaseColor, isDark), padding: 12 };
    const { odsazeni, zaobleni, hrana } = HLAVICKA_MOBIL;
    return {
        position: 'relative',
        zIndex: 20,
        // `--hh` = safe area, ale aspoň 12 px: v prohlížeči bez stavového
        // řádku by obsah jinak lepil na horní hranu okna.
        '--hh': 'max(var(--hlavicka-mobil-horni), 12px)',
        paddingTop: 'calc(var(--hh) + 4px)',
        paddingLeft: `max(${odsazeni}px, env(safe-area-inset-left))`,
        paddingRight: `max(${odsazeni}px, env(safe-area-inset-right))`,
        paddingBottom: 14,
        borderRadius: `0 0 ${zaobleni}px ${zaobleni}px`,
        background: 'var(--surface-base)',
        borderBottom: `${hrana}px solid var(--border-default)`,
        boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.55)' : '0 10px 24px rgba(30,30,50,0.12)',
    };
}

export default function ShiftCardHeader({
    phaseColor,
    title,
    subtitle,
    tym = [],
    onOpenTeam,               // klik na chip stacku → bottom sheet s týmem (nahrazuje onOpenMember/onOpenMessage)
    onCancel,                 // volitelné — malý ✕ vpravo (Starting: zrušit zahájení)
    noStrip = false,          // skryje vlastní fázový proužek (nese ho rodič přes celou sekci)
    isDark,
}) {
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Barevný proužek fáze — vynechá se, pokud ho nese rodič přes
               celou sekci (mobilní cockpit). */}
            {!noStrip && (
                <div style={{
                    width: 4, alignSelf: 'stretch', minHeight: 36,
                    borderRadius: 2, background: phaseColor, flexShrink: 0,
                }} />
            )}

            {/* Název + datum */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2, color: textPrimary, ...ellipsis }}>
                    {title}
                </div>
                {subtitle && (
                    <div style={{ fontSize: 11.5, marginTop: 2, color: textMuted, fontVariantNumeric: 'tabular-nums', ...ellipsis }}>
                        {subtitle}
                    </div>
                )}
            </div>

            {/* Team chip — celý stack je jedno tlačítko, klik otevře seznam kolegů */}
            {onOpenTeam && (
                <button type="button" onClick={onOpenTeam}
                    title={tym.length > 0 ? 'Tým na směně' : 'Žádný kolega na směně'}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
                        padding: '4px 10px 4px 4px', borderRadius: 999,
                        background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                        color: textPrimary, cursor: 'pointer',
                    }}>
                    {tym.length === 0 ? (
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: '50%',
                            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                            color: labelMuted, marginLeft: 2,
                        }}>
                            <Users size={13} />
                        </span>
                    ) : (
                        <span style={{ display: 'inline-flex' }}>
                            {tym.slice(0, 3).map((m, i) => (
                                <span key={m.zamestnanec_id}
                                    style={{
                                        width: 28, height: 28, borderRadius: '50%',
                                        background: avatarGradient(m.zamestnanec_id),
                                        marginLeft: i === 0 ? 0 : -8,
                                        border: `2px solid ${isDark ? '#141418' : '#fff'}`,
                                        color: '#fff', fontSize: 10.5, fontWeight: 700,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                    {initials(m.jmeno, m.prijmeni)}
                                </span>
                            ))}
                        </span>
                    )}
                    <span style={{
                        fontSize: 11.5, fontWeight: 700, color: textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                    }}>
                        {tym.length === 0 ? 'Sám' : `+${tym.length}`}
                    </span>
                    {/* Sám = "jsem na směně sám"; +N = celkem kolegů kromě mě (bez ohledu na to kolik avatarů zobrazujeme). */}
                </button>
            )}

            {/* Zrušit (jen Starting) */}
            {onCancel && (
                <button type="button" onClick={onCancel} title="Zrušit zahájení"
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        background: 'transparent',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                        color: labelMuted, cursor: 'pointer', padding: 0,
                    }}>
                    <X size={14} />
                </button>
            )}
        </div>
    );
}
